import { useEffect, useState } from 'react';

// MOBILE_QUERY matches the Tailwind `sm` breakpoint boundary: below it the
// picker presents as a bottom sheet on BOTH surfaces (owner decision — one
// gesture everywhere), above it as inline or a popover.
export const MOBILE_QUERY = '(max-width: 639px)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

// SHEET_MAX_HEIGHT: below this viewport height, present as a sheet rather
// than attempt a desktop-width popover at all. Derived from a real
// Chromium measurement (Playwright, OH-131) against DateRangePicker's own
// field popover:
//   - the calendar panel's tallest month grid (a six-week month) measures
//     ~454px (getBoundingClientRect, confirmed live — matches the
//     CalendarSkeleton comment's estimate in DateRangePicker.tsx)
//   - the picker trigger itself measures ~53px tall, on both the home hero
//     and the /search sticky bar
//   - GAP (8px, trigger↔panel) and MARGIN (8px, viewport-edge safety,
//     budgeted at BOTH the top and the bottom) come from
//     useAnchoredPosition.ts
// trigger(53) + GAP(8) + panel(454) + MARGIN(8) + MARGIN(8) = 531, rounded
// down to 530: the shortest viewport that can hold the trigger AND the
// tallest panel with a full margin at both ends, budgeting the trigger's
// own top at exactly MARGIN — as close to the top edge as a real trigger
// gets without sitting flush against it (anchor.top = 0 would drop the
// budget to 523, not 531). Confirmed live: shrinking the home hero page's
// popover (the tightest real layout measured — only 9px of headroom above
// its trigger even at full height) started needing the fallback clamp
// right around this same neighbourhood, past ~515px.
//
// This number is a UX threshold now, not a correctness backstop:
// computeAnchoredPosition's placement selection is fit-aware (OH-131 fix
// round 1 — it picks whichever side the panel's clamped bottom actually
// stays inside the viewport for) and `top` is unconditionally
// lower-bounded, so a popover below this height would no longer clip
// off-screen or run off the bottom either way — see that function's own
// comments. Below SHEET_MAX_HEIGHT, even a correctly-placed popover has to
// lean on that clamped fallback (e.g. a panel pinned near the top of the
// viewport, potentially over its own trigger) rather than genuinely having
// room on either side; the sheet reads better than that.
const SHEET_MAX_HEIGHT = 530;

// SHEET_QUERY: narrow OR short. `computeAnchoredPosition` has nowhere to
// put a ~454px popover once the viewport is shorter than SHEET_MAX_HEIGHT,
// regardless of width — a phone in landscape or a short desktop window hits
// this even though it is nowhere near MOBILE_QUERY's narrow-width
// threshold. That was OH-131: the `above` branch subtracted the full panel
// height from the trigger's `top` and went negative, pushing the month
// caption and prev/next arrows off the top of the screen.
//
// A NEW, separate hook — not a height term bolted onto useIsMobile — on
// purpose. useIsMobile/MOBILE_QUERY has a THIRD consumer beyond the two
// pickers: routes/search.tsx reads it to decide where the Sort control
// renders, and that file's own comment explains why it deliberately reuses
// MOBILE_QUERY rather than a second breakpoint of its own — MOBILE_QUERY is
// pinned to Tailwind's `sm` boundary, so the JS gate there and the `sm:`
// classes in that file's JSX agree on the same cutoff BY CONSTRUCTION, not
// by two numbers kept in sync by hand. Adding a height term to MOBILE_QUERY
// would break that: at (e.g.) 800×400 the JS would say "mobile" and try to
// move Sort into the mobile Filters row, while the CSS — width-only, still
// desktop at 800px — renders its desktop row with no Sort in it at all.
// "Should I present as a sheet?" (the pickers) and "is this a phone?" (Sort
// placement) are different questions that happened to share an answer until
// a wide-but-short viewport came along; this hook exists so nobody merges
// them back into one and reintroduces that bug.
export const SHEET_QUERY = `${MOBILE_QUERY}, (max-height: ${SHEET_MAX_HEIGHT}px)`;

export function useShouldPresentAsSheet(): boolean {
  return useMediaQuery(SHEET_QUERY);
}
